import React, { useEffect, useMemo, useRef, useState } from "react";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import DtvModal from "@directv-digital/dds.ui.dtv-modal";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import Card from "@mui/material/Card";
import CardMedia from '@mui/material/CardMedia';
import DtvButton from "@directv-digital/dds.ui.dtv-button";
import {
  Analytics,
  fireModalOpenCloseEventGA
} from "@directv-digital/dds.services.analytics-service";
import { safeHttpUrl } from "src/utils/UtilityFunctions";
import {
  absoluteAnalyticsUrl,
  fireShowModalLinkClick,
  fireShowModalPageView,
  getCurrentPageUrl,
  getShowModalPageUrl,
  getShowModalPosition
} from "../show-carousal.analytics";

interface DtvMediaModalProps {
    analytics?: Analytics;
    id?: string;
    defaultImageUrl?: any;
    imageUrl?: any;
    altImage?: string;
    imgWidth?: any;
    imgHeight?: any;
    heading?: string;
    dominantColor?:string;
    subheading?: string;
    body?: string;
    modalOpen?: boolean;
    closeMediaModal?: any;
    contentType?: any;
    resourceId?: any;
    cta1?: any;
    cta2?: any;
    cropImage?: boolean;
    enableChannelInfo?: boolean;
}

export function DtvMediaModal(props: DtvMediaModalProps) {
  try {
    const [open, setOpen] = useState(false);
    // Deliberately keyed on the prop alone. With `open` in here too, the effect re-ran on every
    // local close and forced the modal back open while the parent still had modalOpen set —
    // which DtvModal reports as another modal open.
    // The contract this puts on the parent: clear modalOpen when closing (both carousel
    // variants do, via closeMediaModal). A parent that leaves it set cannot reopen the modal,
    // because the prop never changes and this effect never runs again.
    useEffect(() => {
      setOpen(!!props?.modalOpen);
    }, [props?.modalOpen]);
    // Every interaction inside the modal is reported against the show it belongs to.
    const modalPosition = getShowModalPosition(props?.heading);
    // Recomputed on the open transition, which also refreshes it after a client-side
    // route change. It is the typeof-window guard inside the helper, not this dependency,
    // that keeps the server render from reporting a url.
    const modalUrl = useMemo(() => getShowModalPageUrl(), [open]);
    // DtvModal re-runs its modal_status effect whenever this object's identity changes, so it
    // must only change when a new show opens. It cannot be derived from the current heading:
    // closing clears the parent's modal data one commit before `open` turns false, so the
    // heading — and with it the position — changes while DtvModal still sees an open modal,
    // which it would report as another modal_status open.
    // Adjusted during render, not in an effect, so DtvModal never gets a commit where the
    // modal is open but the position still belongs to the previous show.
    const [openPosition, setOpenPosition] = useState(modalPosition);
    if (props?.modalOpen && openPosition !== modalPosition) {
      setOpenPosition(modalPosition);
    }
    const modalOpenAnalytics = useMemo(
      () => ({
        dim_name: "open",
        dim_value: openPosition
      }),
      [openPosition]
    );
    // A backdrop click reaches onClose twice — once through DtvModal's ClickAwayListener
    // and once through the underlying MUI Modal — but it is one close to report.
    const closeReported = useRef(false);
    // DtvModal registers its Esc listener once, on mount, so the close handler it invokes is
    // this component's first-render closure — where the modal is shut and the show unknown.
    // Close reporting therefore has to read both through refs, and must not fire at all when
    // no modal is open: this component stays mounted for the life of the carousel, so every
    // Esc press on the page reaches that listener.
    const openRef = useRef(false);
    const positionRef = useRef(modalPosition);
    useEffect(() => {
      openRef.current = open;
      if (open) {
        closeReported.current = false;
      }
    }, [open]);
    useEffect(() => {
      positionRef.current = modalPosition;
    }, [modalPosition]);
    // AC2 — the modal's page view. Keyed on `open`, the state DtvModal is actually handed,
    // rather than props.modalOpen: the two are a commit apart, and the prop is the parent's
    // intent while `open` is what is on screen.
    // openPosition rather than modalPosition because modalPosition ALSO changes when the parent
    // clears its modal data during close: keyed on it, the commit between "show data gone" and
    // "open false" would push a second page view. openPosition is frozen at that point (see the
    // guard above), so it cannot. Both dependencies report a show-to-show switch WITHOUT a
    // close, which is wanted — DtvModal already treats modal_status open that way — so that is
    // not what decides between them.
    // The dependency only decides WHEN to report; the payload comes from props.heading. The
    // two cannot disagree, because openPosition is reassigned only in a render where
    // props.modalOpen is true — exactly the render whose heading already names the new show.
    // That rests on one parent contract: modalOpen and heading must arrive in the SAME state
    // update. Both carousel variants do (a single setState with the whole show object), and
    // the props here are spread from an untyped object so nothing can enforce it — setting
    // modalOpen a commit early costs an extra page view against the bare
    // `modal_show carousel` (pinned by spec).
    useEffect(() => {
      if (!open) {
        return;
      }
      fireShowModalPageView(props?.heading);
    }, [open, openPosition]);
    const getRedirectUrl = (label: string, target: string, targetUrl?: string) => {
      if (targetUrl) {
        // targeturl can come from CMS (incl. per-show ctaOverrides); only allow http(s) so a
        // javascript:/data: url can't execute in-origin via window.open.
        const safeUrl = safeHttpUrl(targetUrl);
        if (!safeUrl) {
          console.warn("[DtvMediaModal] blocked non-http(s) CTA url:", targetUrl);
          return;
        }
        handleOnClick(label, safeUrl);
        window.open(safeUrl, target);
      } else if (typeof window !== 'undefined'){
        const deviceType =
                  navigator.userAgent.match("iPad") ||
                  navigator.userAgent.match("iPhone") ||
                  navigator.userAgent.match("iPod") ||
                  navigator.userAgent.match("Android");

        const ovUrl =
                  props?.contentType === 'MOVIE' ?
                    `https://stream.directv.com/discover/MOVIE/program/VIDEO_CONTENT/${props?.resourceId}` :
                    props?.contentType === 'EPISODE' ?
                    `https://stream.directv.com/discover/EPISODE/program/VIDEO_CONTENT/${props?.resourceId}` :
                      `https://stream.directv.com/discover/series/content/VIDEO_CONTENT/${props?.resourceId}`

        const url = !deviceType ? ovUrl : '/guide/home/?redirectToOv=true'
        handleOnClick(label, url);
        window.open(url, target);
      }
    }
    // AC3. The label comes from the CMS, so the reported name is whatever the button says —
    // the emitter lower cases it and drops the event when the label is blank.
    const handleOnClick = (name: any, url: any) => {
      fireShowModalLinkClick({
        linkName: name,
        linkPosition: modalPosition,
        linkDestination: absoluteAnalyticsUrl(url)
      });
    }
    const reportClose = () => {
      if (!openRef.current || closeReported.current) {
        return;
      }
      closeReported.current = true;
      const linkPosition = positionRef.current;
      // AC4 — a link click paired with the modal_status close below.
      fireShowModalLinkClick({
        linkName: "close",
        linkPosition,
        // Closing does not navigate, so the destination is the page behind the modal.
        linkDestination: getCurrentPageUrl()
      });
      fireModalOpenCloseEventGA({
        dim_name: "close",
        dim_value: linkPosition
      });
    };
    const onClose = event => {
      // DtvModal's Esc listener has no open check and this component stays mounted for the
      // life of the carousel, so every Esc press on the page lands here. Bail before touching
      // parent state, or each one re-renders the whole carousel for nothing.
      if (!openRef.current) {
        return;
      }
      reportClose();
      props?.closeMediaModal?.();
      if (event?.preventDefault) {
        event.preventDefault();
      }
      // Esc arrives here from a window listener, so the event is not always an element event.
      // eslint-disable-next-line eqeqeq
      if (event?.target?.alt == "mediaModalCloseIcon") {
        setOpen(false);
      }
    };
    const styles = {
      shortImageView: {
        aspectRatio: "3/1",
        backgroundColor: `rgb(${props?.dominantColor})`,
        objectPosition: "top",
        marginTop: {
          md: "-56px"
        }
      },
      fullImageView: {
        aspectRatio: "16/9", // : "3/4"
        backgroundColor: `rgb(${props?.dominantColor})`
      }
    }
    return (
            <>
                <DtvModal
                    altText="mediaModalCloseIcon"
                    // DtvModal reports the modal page view from modalUrl + analytics through
                    // the DataMappingInterface bridge, and the modal_status open event from
                    // modalOpenAnalytics. That bridge page load is kept alongside the AC2
                    // page_view push above, so the open is reported on both paths — if the
                    // container maps both to a GA4 page view, the open counts twice.
                    analytics={{ contentFriendlyName: modalPosition }}
                    closeIconUrl="https://www.directv.com/dtvassets/sales/uf/icons/Tertiary_icon_only%20button_onDark_white_48.svg"
                    handleClose={onClose}
                    hideScrollbar={true}
                    id={props?.id}
                    modalOpenAnalytics={modalOpenAnalytics}
                    modalUrl={modalUrl}
                    open={open}
                    tabIndex={0}
                    closeIconStyle={{
                        height: {
                          xs: '36px',
                          md: '48px'
                        },
                        width: {
                          xs: '36px',
                          md: '48px'
                        }
                      }}
                    modalBodyStyle={{
                        backgroundColor: "#FFFFFF",
                        width: '100%',
                        maxHeight: '90vh',
                        ml: '0 !important',
                        mr: '0 !important',
                        mb: '0 !important',
                        borderRadius: "inherit"
                      }}
                      modalStyle={{
                        backgroundColor: "#FFFFFF",
                        maxWidth: {
                          xs: "100%",
                          sm: "600px",
                          md: "900px"
                        },
                        maxHeight: "90%",
                        overflow: "hidden",
                        height: "fit-content",
                        borderRadius: {
                          xs: "10px 10px 0 0",
                          sm: "10px"
                        },
                        "& > .MuiBox-root:first-child": {
                          position: "absolute",
                          right: "10px",
                          top: "10px",
                          zIndex: 1
                        }
                      }}
                      onClose={e => {
                      onClose(e)
                      setOpen(false);
                    }}
                >
                    <>
                        <Card
                            sx={{
                                width: '100%',
                                backgroundColor: `rgb(${props?.dominantColor})`,
                                borderRadius: "0px !important"
                            }}
                        >
                            <CardMedia
                                alt={props?.altImage}
                                component={'img'}
                                loading="lazy"
                                src={props?.imageUrl}
                                sx={props?.cropImage ? { ...styles.shortImageView } : { ...styles.fullImageView }}
                                onError={e => {
                                    const target = e.target as HTMLImageElement;
                                    target.onerror = null;
                                    target.src = props?.defaultImageUrl ? props?.defaultImageUrl : props?.altImage;
                                }}
                            />
                        </Card>
                        <DtvBox
                            sx={{
                            width: '100%',
                            padding: {
                                xs: "0 20px 20px 20px",
                                sm: "0 32px 24px 32px",
                                md: "0 48px 32px 48px"
                            }
                            }}
                        >
                            {props?.heading &&
                                <DtvText
                                    color="#102641"
                                    content={props?.heading}
                                    variant='subhead1medium'
                                    sx={{
                                    display: "block",
                                    margin: {
                                        xs: "20px 0 16px 0",
                                        sm: "32px 0 20px 0",
                                        md: "40px 0 24px 0"
                                    },
                                    fontSize: {
                                        xs: "24px",
                                        sm: "30px",
                                        md: props?.enableChannelInfo ? "30px" : "36px"
                                    },
                                    whiteSpace: "pre-wrap"
                                    }}
                                />
                            }
                            {props?.subheading &&
                                <DtvText
                                    content={props?.subheading}
                                    sx={{
                                        display: "block",
                                        fontSize: {
                                            xs: "20px",
                                            md: "20px"
                                        },
                                        whiteSpace: "pre-wrap"
                                    }}
                                    color='#102641'
                                    // variant='subhead2medium'
                                    variant='body1'
                                />
                            }
                            {props?.body &&
                                <DtvText
                                    color='#102641'
                                    variant='body1'
                                    content={
                                        props?.body
                                    }
                                    sx={{
                                        display: "block",
                                        whiteSpace: "pre-wrap",
                                        marginTop: props?.enableChannelInfo ? "2px" : "24px",
                                        lineHeight: "20.8px",
                                        fontSize: {
                                            xs: "16px",
                                            md: "16px"
                                        }
                                    }}
                                />
                            }
                            {(props?.cta1 || props?.cta2) &&
                                <DtvBox
                                    sx={{
                                        display: {
                                            xs: "block",
                                            sm: "flex"
                                        },
                                        justifyContent: {
                                            xs: "center",
                                            sm: "center",
                                            md: "left"
                                        },
                                        justifyItems: "flex-start",
                                        marginTop: {
                                            xs: "20px",
                                            sm: "24px",
                                            md: "32px"
                                        }
                                    }}
                                >
                                    {props?.cta1 &&
                                        <DtvButton
                                            ariaLabel={props?.cta1?.label}
                                            component="a"
                                            id={`card-cta-watch_now_btn-${props?.cta1?.id}`}
                                            label={props?.cta1?.label}
                                            mode={props?.cta1?.mode || "dark"}
                                            promo={props?.cta1?.promo}
                                            sx={{
                                                display: "flex",
                                                flexDirection: "row",
                                                flexWrap: "wrap",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                verticalAlign: "baseline",
                                                textAlign: "center",
                                                minWidth: '180px',
                                                marginBottom: {
                                                    xs: "8px",
                                                    sm: "0px",
                                                    md: "0px"
                                                },
                                                mr: {
                                                    xs: "0px",
                                                    sm: "16px",
                                                    xl: "16px"
                                                },
                                                ml: "0px"
                                            }}
                                            onClick={() => {
                                              if (props?.cta1?.targeturl) {
                                                getRedirectUrl(props?.cta1?.label, props?.cta1?.target || "_self", props.cta1?.targeturl)
                                              } else {
                                                getRedirectUrl(props?.cta1?.label, props?.cta1?.target || "_self")
                                              }
                                            }}
                                        />
                                    }
                                    {props?.cta2 &&
                                        <DtvButton
                                            ariaLabel={props?.cta2?.label}
                                            component="a"
                                            id={`card-secondaryCta-${props?.cta2?.id}`}
                                            label={props?.cta2?.label}
                                            onClick={() => {
                                              if (props?.cta2?.targeturl) {
                                                getRedirectUrl(props?.cta2?.label, props?.cta2?.target || "_self", props?.cta2?.targeturl)
                                              } else {
                                                getRedirectUrl(props?.cta2?.label, props?.cta2?.target || "_self")
                                              }
                                            }}
                                            // href={props?.cta2?.targeturl}
                                            // target={props?.cta2?.target}
                                            mode={props?.cta2?.mode || "dark"}
                                            promo={props?.cta2?.promo}
                                            variant={props?.cta2?.variant || "uxsecondary"}
                                            sx={{
                                                display: "flex",
                                                flexDirection: "row",
                                                flexWrap: "wrap",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                verticalAlign: "baseline",
                                                textAlign: "center",
                                                minWidth: '180px',
                                                marginTop: {
                                                    xs: "8px",
                                                    sm: "0px",
                                                    md: "0px"
                                                },
                                                mr: "0px",
                                                ml: {
                                                    xs: "0px",
                                                    sm: "16px",
                                                    xl: "16px"
                                                }
                                            }}
                                        />
                                    }
                                </DtvBox>
                            }
                        </DtvBox>
                    </>
                </DtvModal>
            </>
    );
  } catch(error) {
    return <ErrorDisplay component="DTV Media Modal" error={error} />;
  }
}
